import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ToolGuard } from "@/components/ToolGuard";

import Login from "@/pages/Login";
import PublicRequest from "@/pages/PublicRequest";
import PublicIntake from "@/pages/PublicIntake";
import CatNatPublic from "@/pages/CatNatPublic";
import CatNatInbox from "@/pages/CatNatInbox";
import ProtezioneFamiglia from "@/pages/ProtezioneFamiglia";
import ProductLanding from "@/pages/ProductLanding";
import ProposalAccept from "@/pages/ProposalAccept";
import ProductHubB2B from "@/pages/partner/ProductHubB2B";
import Landings from "@/pages/partner/Landings";
import AdesioneDistanza from "@/pages/AdesioneDistanza";
import IntakeManager from "@/pages/partner/IntakeManager";

import PartnerLayout from "@/pages/partner/PartnerLayout";
import ProposalBundle from "@/pages/ProposalBundle";
import CatNatLanding from "@/pages/partner/CatNatLanding";
import Worklist from "@/pages/partner/Worklist";
import NewRequest from "@/pages/partner/NewRequest";
import NewPratica from "@/pages/partner/NewPratica";
import Anagrafiche from "@/pages/partner/Anagrafiche";
import QuoteEngine from "@/pages/partner/QuoteEngine";
import MrvQuoteEngine from "@/pages/partner/MrvQuoteEngine";
import InfQuoteEngine from "@/pages/partner/InfQuoteEngine";
import CatNatQuoteEngine from "@/pages/partner/CatNatQuoteEngine";
import MultirischiBeniQuoteEngine from "@/pages/partner/MultirischiBeniQuoteEngine";
import CasaQuoteEngine from "@/pages/partner/CasaQuoteEngine";
import { ProductGuard } from "@/components/ProductGuard";
import ProductHub from "@/pages/partner/ProductHub";
import TechAssistant from "@/pages/TechAssistant";
import RelazioneFrameworks from "@/pages/admin/RelazioneFrameworks";
import CatNatRates from "@/pages/admin/CatNatRates";
import QuotesList from "@/pages/partner/QuotesList";
import CaseFile from "@/pages/partner/CaseFile";
import Provvigioni from "@/pages/Provvigioni";

import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import Partners from "@/pages/admin/Partners";
import ProductFactory from "@/pages/admin/ProductFactory";
import RequestsRegistry from "@/pages/admin/RequestsRegistry";
import QuotesRegistry from "@/pages/admin/QuotesRegistry";
import ReteSolidaleAdmin from "@/pages/admin/ReteSolidaleAdmin";
import TestDataAdmin from "@/pages/admin/TestDataAdmin";
import AccordiAdmin from "@/pages/admin/AccordiAdmin";
import ReteGestione from "@/pages/partner/ReteGestione";
import AccordoLanding from "@/pages/accordi/AccordoLanding";
import AccordoAccesso from "@/pages/accordi/AccordoAccesso";
import AccordoArea from "@/pages/accordi/AccordoArea";
import AccordoPreventivo from "@/pages/accordi/AccordoPreventivo";
import { ReferenteLogin, ReferenteAttiva } from "@/pages/referente/ReferenteAuth";
import ReferenteDashboard from "@/pages/referente/ReferenteDashboard";
import ApiTokensAdmin from "@/pages/admin/ApiTokensAdmin";
import ReleaseNotesAdmin from "@/pages/admin/ReleaseNotesAdmin";
import TrattativeAdmin from "@/pages/admin/TrattativeAdmin";
import CoopSaluteQuote from "@/pages/CoopSaluteQuote";
import DoQuote from "@/pages/DoQuote";
import ReteSolidale from "@/pages/ReteSolidale";
import CooperativeConvenzionate from "@/pages/CooperativeConvenzionate";
import AdesioniRegistry from "@/pages/admin/AdesioniRegistry";
import ManualsAdmin from "@/pages/admin/ManualsAdmin";
import LandingsAdmin from "@/pages/admin/LandingsAdmin";
import Guida from "@/pages/Guida";
import Audit from "@/pages/admin/Audit";
import Settings from "@/pages/admin/Settings";
import { PrivacyPolicy, CookiePolicy, AiPolicy } from "@/pages/Legal";
import { CookieBanner } from "@/components/CookieBanner";
import FormulaServiceCalc from "@/pages/partner/FormulaServiceCalc";
import FormulaSelfCalc from "@/pages/partner/FormulaSelfCalc";
import FormulaServicePublic from "@/pages/FormulaServicePublic";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <CookieBanner />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookie" element={<CookiePolicy />} />
          <Route path="/ai" element={<AiPolicy />} />
          <Route path="/r/:codice" element={<PublicRequest />} />
          <Route path="/modulo/:token" element={<PublicIntake />} />
          <Route path="/catnat" element={<CatNatPublic />} />
          <Route path="/protezione-famiglia" element={<ProtezioneFamiglia />} />
          <Route path="/prodotti/:slug" element={<ProductLanding />} />
          <Route path="/p/:shortId" element={<FormulaServicePublic />} />
          <Route path="/adesione/:token" element={<AdesioneDistanza />} />
          <Route path="/proposta/:token" element={<ProposalAccept />} />
          <Route path="/proposta-unica/:token" element={<ProposalBundle />} />
          <Route path="/rete-solidale" element={<ReteSolidale />} />
          <Route path="/cooperative-convenzionate" element={<CooperativeConvenzionate />} />
          <Route path="/accordi" element={<AccordoAccesso />} />
          <Route path="/accordi/preventivo/:token" element={<AccordoPreventivo />} />
          <Route path="/accordi/:slug" element={<AccordoLanding />} />
          <Route path="/accordi/:slug/area" element={<AccordoArea />} />
          <Route path="/referente" element={<ReferenteLogin />} />
          <Route path="/referente/attiva/:token" element={<ReferenteAttiva />} />
          <Route path="/referente/dashboard" element={<ReferenteDashboard />} />
          <Route path="/catnat-inbox" element={<ProtectedRoute catnatOnly><CatNatInbox /></ProtectedRoute>} />
          <Route path="/catnat-inbox/preventivo/:id" element={<ProtectedRoute catnatOnly><CaseFile /></ProtectedRoute>} />

          <Route path="/" element={<ProtectedRoute><PartnerLayout /></ProtectedRoute>}>
            <Route index element={<Worklist />} />
            <Route path="pratica/nuova" element={<NewPratica />} />
            <Route path="richieste/nuova" element={<ToolGuard tool="nuova_richiesta"><NewRequest /></ToolGuard>} />
            <Route path="anagrafiche" element={<Anagrafiche />} />
            <Route path="quota" element={<ProductHub />} />
            <Route path="link-cliente" element={<ToolGuard tool="link_cliente"><IntakeManager /></ToolGuard>} />
            <Route path="quota/nuovo" element={<ProductGuard code="rc_economia_solidale"><QuoteEngine /></ProductGuard>} />
            <Route path="quota/mrv" element={<ProductGuard code="mrv_volontariato"><MrvQuoteEngine /></ProductGuard>} />
            <Route path="quota/infortuni" element={<ProductGuard code="infortuni_economia_solidale"><InfQuoteEngine /></ProductGuard>} />
            <Route path="quota/catnat" element={<ProductGuard code="cat_nat_impresa"><CatNatQuoteEngine /></ProductGuard>} />
            <Route path="quota/multirischi" element={<ProductGuard code="multirischi_beni"><MultirischiBeniQuoteEngine /></ProductGuard>} />
            <Route path="quota/casa" element={<ProductGuard code="casa_multirischi"><CasaQuoteEngine /></ProductGuard>} />
            <Route path="quota/cooperazione-salute" element={<ProductGuard code="cooperazione_salute"><CoopSaluteQuote /></ProductGuard>} />
            <Route path="quota/do" element={<ProductGuard code="d_o"><DoQuote /></ProductGuard>} />
            <Route path="formula-service" element={<ProductGuard code="formula_service"><FormulaServiceCalc /></ProductGuard>} />
            <Route path="formula-self" element={<ProductGuard code="protezione_famiglia"><FormulaSelfCalc /></ProductGuard>} />
            <Route path="preventivi" element={<QuotesList key="all" />} />
            <Route path="proposte" element={<QuotesList key="proposte" vista="proposte" />} />
            <Route path="cat-nat" element={<CatNatLanding />} />
            <Route path="hub-prodotti" element={<ToolGuard tool="marketing"><ProductHubB2B /></ToolGuard>} />
            <Route path="landing" element={<ToolGuard tool="marketing"><Landings /></ToolGuard>} />
            <Route path="preventivi/:id" element={<CaseFile />} />
            <Route path="provvigioni" element={<Provvigioni />} />
            <Route path="assistente" element={<ToolGuard tool="assistente"><TechAssistant /></ToolGuard>} />
            <Route path="guida" element={<ToolGuard tool="guida"><Guida /></ToolGuard>} />
            <Route path="rete-gestione" element={<ReteGestione />}>
              <Route index element={<Navigate to="accordi" replace />} />
              <Route path="accordi" element={<AccordiAdmin />} />
              <Route path="convenzioni" element={<ReteSolidaleAdmin />} />
            </Route>
          </Route>

          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="partner" element={<Partners />} />
            <Route path="prodotti" element={<ProductFactory />} />
            <Route path="richieste" element={<RequestsRegistry />} />
            <Route path="preventivi" element={<QuotesRegistry />} />
            <Route path="preventivi/:id" element={<CaseFile />} />
            <Route path="provvigioni" element={<Provvigioni />} />
            <Route path="adesioni" element={<AdesioniRegistry />} />
            <Route path="audit" element={<Audit />} />
            <Route path="impostazioni" element={<Settings />} />
            <Route path="assistente" element={<TechAssistant />} />
            <Route path="framework-relazioni" element={<RelazioneFrameworks />} />
            <Route path="cat-nat" element={<CatNatLanding />} />
            <Route path="catnat-tassi" element={<CatNatRates />} />
            <Route path="link-cliente" element={<IntakeManager />} />
            <Route path="manuali" element={<ManualsAdmin />} />
            <Route path="landing" element={<LandingsAdmin />} />
            <Route path="rete-solidale" element={<ReteSolidaleAdmin />} />
            <Route path="api-integrazione" element={<ApiTokensAdmin />} />
            <Route path="novita" element={<ReleaseNotesAdmin />} />
            <Route path="trattative" element={<TrattativeAdmin />} />
            <Route path="dati-prova" element={<TestDataAdmin />} />
            <Route path="accordi" element={<AccordiAdmin />} />
            <Route path="quota/nuovo" element={<QuoteEngine />} />
            <Route path="quota/mrv" element={<MrvQuoteEngine />} />
            <Route path="quota/infortuni" element={<InfQuoteEngine />} />
            <Route path="quota/catnat" element={<CatNatQuoteEngine />} />
            <Route path="quota/multirischi" element={<MultirischiBeniQuoteEngine />} />
            <Route path="quota/casa" element={<CasaQuoteEngine />} />
            <Route path="quota/cooperazione-salute" element={<CoopSaluteQuote />} />
            <Route path="quota/do" element={<DoQuote />} />
            <Route path="formula-service" element={<FormulaServiceCalc />} />
            <Route path="formula-self" element={<FormulaSelfCalc />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
